import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../api/axios';
import Modal from '../components/shared/Modal';
import ConfirmModal from '../components/shared/ConfirmModal';
import { format } from 'date-fns';

export default function Categories() {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(1);

  const { data: cats = [], isLoading } = useQuery({
    queryKey: ['cats-all'],
    queryFn: () => api.get('/categories/all').then(r => r.data),
  });

  const saveMut = useMutation({
    mutationFn: () => editCat
      ? api.put(`/categories/${editCat.id}`, { name, is_active: isActive })
      : api.post('/categories', { name }),
    onSuccess: () => { toast.success(editCat ? 'Category updated' : 'Category added'); qc.invalidateQueries(['cats-all']); qc.invalidateQueries(['categories']); setModalOpen(false); },
  });

  const deleteMut = useMutation({
    mutationFn: id => api.delete(`/categories/${id}`),
    onSuccess: () => { toast.success('Category deleted'); qc.invalidateQueries(['cats-all']); qc.invalidateQueries(['categories']); },
  });

  const open = (cat = null) => {
    setEditCat(cat);
    setName(cat?.name || '');
    setIsActive(cat?.is_active ?? 1);
    setModalOpen(true);
  };

  return (
    <div>
      <div className="page-header">
        <h2>Category Management</h2>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => open()}>+ Add Category</button>
      </div>
      <div className="tbl-wrap">
        <table>
          <thead><tr><th>Category Name</th><th>Created</th><th>Active</th><th>Actions</th></tr></thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={4} style={{ textAlign: 'center', padding: 30 }}>Loading…</td></tr>
            ) : cats.map(c => (
              <tr key={c.id}>
                <td><strong>{c.name}</strong></td>
                <td style={{ color: '#6c757d', fontSize: 12 }}>{c.created_at ? format(new Date(c.created_at), 'dd MMM yyyy') : '—'}</td>
                <td><div className={`toggle ${c.is_active ? 'on' : ''}`}
                  onClick={() => api.put(`/categories/${c.id}`, { is_active: c.is_active ? 0 : 1 }).then(() => { qc.invalidateQueries(['cats-all']); qc.invalidateQueries(['categories']); })} role="presentation" /></td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button type="button" className="ico-btn" onClick={() => open(c)}>
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M11 2l3 3-9 9H2v-3l9-9z" /></svg>
                    </button>
                    <button type="button" className="ico-btn" onClick={() => setDeleteId(c.id)}>
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="#dc3545" strokeWidth="1.5"><polyline points="2,4 14,4" /><path d="M5 4V2h6v2M6 7v5M10 7v5" /><rect x="3" y="4" width="10" height="10" rx="1" /></svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editCat ? 'Edit Category' : 'Add Category'}>
        <div className="form-group"><label>Category Name</label><input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Web Banner" /></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 13 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#495057' }}>Active</label>
          <div className={`toggle ${isActive ? 'on' : ''}`} onClick={() => setIsActive(v => (v ? 0 : 1))} role="presentation" />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" className="btn btn-outline" onClick={() => setModalOpen(false)}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={() => saveMut.mutate()} disabled={!name || saveMut.isPending}>
            {saveMut.isPending ? 'Saving…' : 'Save Category'}
          </button>
        </div>
      </Modal>

      <ConfirmModal open={!!deleteId} onClose={() => setDeleteId(null)}
        title="Delete this category?" message="Categories with existing jobs cannot be deleted."
        confirmLabel="Delete" danger onConfirm={() => deleteMut.mutate(deleteId)} />
    </div>
  );
}
